import { Component, computed, effect, ElementRef, inject, input, Input, OnDestroy, signal, untracked, viewChild } from '@angular/core';
import { CreateVideoViewDto, VideoCreatorResponseDto, VideoResponseDto } from '../../common/general-types/videos.dto';
import { VideoFormatEnum } from '../../common/general-types/briefs.dto';
import { VideoService } from '../../services/video/video.service';
import { DeviceInfoService } from '../../services/device-info/device-info.service';
import { PosthogService } from '../../services/analytics/posthog.service';
import Hls, { ErrorData } from "hls.js";
import { firstValueFrom } from 'rxjs';

export const VIDEO_LOAD_TIMEOUT_MS = 30000;

@Component({
  selector: 'atom-video-with-controls',
  standalone: true,
  imports: [],
  templateUrl: './video-with-controls.component.html',
  styleUrl: './video-with-controls.component.css'
})
export class VideoWithControlsComponent implements OnDestroy {

  video = input<VideoCreatorResponseDto | VideoResponseDto | undefined>();
  @Input() videoFormat!: VideoFormatEnum;
  videoPlayer = viewChild<ElementRef<HTMLVideoElement>>('videoPlayer');

  currentTime = signal<number>(0);
  duration = signal<number>(0);
  isLoading = signal<boolean | undefined>(undefined);
  isFullScreenMode = signal<boolean>(false);
  isPlaying = signal<boolean>(false);
  isScrubbing = signal<boolean>(false);
  loadError = signal<string | undefined>(undefined);
  platformType = signal<string | undefined>(undefined);
  progress = signal<number>(0);

  /** Trav's Curiosity */
  videoPlayStart: Date | undefined;
  videoPlayEnd: Date | undefined;
  public readonly videoFormatEnum = VideoFormatEnum;

  private _videoService = inject(VideoService);
  private _deviceInfo = inject(DeviceInfoService);
  private _posthog = inject(PosthogService);

  private readonly hlsUrl = computed(() => this.video()?.hlsUrl?.trim() || undefined);
  private readonly fullUrl = computed(() => this.video()?.fullUrl);
  private hls: Hls | null = null;
  private mediaErrorRecoveryTried = false;
  private usingFallback = false;
  private loadTimeout: ReturnType<typeof setTimeout> | undefined;

  constructor(private videoService: VideoService, private elementRef: ElementRef) {
    effect(() => {
      this.isFullScreenMode.set(this._videoService.isVideoFullScreenOpen());
      this.platformType.set(this._deviceInfo.platformSignal());
      setTimeout(() => {
        const videoEl = this.videoPlayer()?.nativeElement;
        if (videoEl) {
          this._videoService.applyZoomIfNeeded(videoEl);
        }
      }, 100);
    });

    // Rebuild the stream each time the player element or the video source changes.
    effect((onCleanup) => {
      const videoEl = this.videoPlayer()?.nativeElement;
      const hlsUrl = this.hlsUrl();
      const fullUrl = this.fullUrl();
      if (!videoEl) {
        return;
      }
      untracked(() => this.loadSource(videoEl, hlsUrl, fullUrl));
      onCleanup(() => this.teardownSource());
    });
  }

  retryLoad(): void {
    const videoEl = this.videoPlayer()?.nativeElement;
    if (videoEl) {
      this.teardownSource();
      this.loadSource(videoEl, this.hlsUrl(), this.fullUrl());
    }
  }

  onLoadedMetadata(videoEl: HTMLVideoElement): void {
    this.duration.set(videoEl.duration);
    this._videoService.applyZoomIfNeeded(videoEl);
  }

  onCanPlay(): void {
    this.clearLoadTimeout();
    this.isLoading.set(false);
    this.loadError.set(undefined);
  }

  onVideoError(videoEl: HTMLVideoElement): void {
    // hls.js owns error recovery while it is attached, and an empty src gives no MediaError.
    if (this.hls || !videoEl.error) {
      return;
    }
    this.handleFatalError('media_element_error', { media_error_code: videoEl.error.code });
  }

  onTimeUpdate(videoEl: HTMLVideoElement): void {
    if (!this.isScrubbing() && videoEl.duration) {
      this.currentTime.set(videoEl.currentTime);
      this.progress.set((videoEl.currentTime / videoEl.duration) * 100);
    }
  }

  private loadSource(videoEl: HTMLVideoElement, hlsUrl: string | undefined, fullUrl: string | undefined, fallback = false): void {
    this.usingFallback = fallback;
    this.mediaErrorRecoveryTried = false;
    this.isLoading.set(true);
    this.loadError.set(undefined);
    this.currentTime.set(0);
    this.duration.set(0);
    this.progress.set(0);
    this.startLoadTimeout(videoEl);

    if (hlsUrl && Hls.isSupported()) {
      const hls = new Hls();
      hls.on(Hls.Events.ERROR, (_, data) => this.onHlsError(hls, data));
      hls.loadSource(hlsUrl);
      hls.attachMedia(videoEl);
      this.hls = hls;
    } else if (hlsUrl && videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      videoEl.src = hlsUrl;
    } else if (fullUrl) {
      videoEl.src = fullUrl;
    }
  }

  private onHlsError(hls: Hls, data: ErrorData): void {
    if (!data.fatal || hls !== this.hls) {
      return;
    }
    if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !this.mediaErrorRecoveryTried) {
      this.mediaErrorRecoveryTried = true;
      hls.recoverMediaError();
      return;
    }
    this.handleFatalError('hls_fatal_error', { hls_error_type: data.type, hls_error_details: data.details });
  }

  private handleFatalError(reason: string, properties: Record<string, unknown> = {}): void {
    const hlsUrl = this.hlsUrl();
    const fullUrl = this.fullUrl();
    const canFallBack = !!hlsUrl && !this.usingFallback && !!fullUrl && fullUrl !== hlsUrl;
    this.reportLoadFailure(reason, { ...properties, fell_back_to_full_url: canFallBack });

    const videoEl = this.videoPlayer()?.nativeElement;
    if (canFallBack && videoEl) {
      this.teardownSource();
      this.loadSource(videoEl, undefined, fullUrl, true);
      return;
    }

    this.clearLoadTimeout();
    this.isLoading.set(false);
    this.loadError.set('Failed to load video');
  }

  private startLoadTimeout(videoEl: HTMLVideoElement): void {
    this.clearLoadTimeout();
    this.loadTimeout = setTimeout(() => {
      if (!this.isLoading()) {
        return;
      }
      // Some browsers (iOS Safari) defer media data until the user presses play.
      if (videoEl.readyState >= HTMLMediaElement.HAVE_METADATA) {
        this.isLoading.set(false);
        return;
      }
      this.reportLoadFailure('load_timeout', { ready_state: videoEl.readyState, network_state: videoEl.networkState });
      this.isLoading.set(false);
      this.loadError.set('The video took too long to load');
    }, VIDEO_LOAD_TIMEOUT_MS);
  }

  private clearLoadTimeout(): void {
    clearTimeout(this.loadTimeout);
    this.loadTimeout = undefined;
  }

  private reportLoadFailure(reason: string, properties: Record<string, unknown>): void {
    this._posthog.posthog.captureException(new Error(`Video failed to load: ${reason}`), {
      video_load_failure_reason: reason,
      video_id: this.video()?.id,
      video_source: this.usingFallback || !this.hlsUrl() ? 'full_url' : 'hls',
      ...properties,
    });
  }

  private teardownSource(): void {
    this.clearLoadTimeout();
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
  }

  togglePlayPause(video: HTMLVideoElement): void {
    video.paused ? video.play() : video.pause();
  }

  onPlay(): void {
    console.log('onPlay');
    this.isPlaying.set(true);
    this.videoPlayStart = new Date();
  }

  onPause(): void {
    console.log('onPause');
    this.isPlaying.set(false);
    this.videoPlayEnd = new Date();
    this.saveVideoView();
  }

  async saveVideoView() {

    console.log('saveVideoView() -> ', this.video());
    const videoId = this.video()?.id;
    if (this.videoPlayStart && this.videoPlayEnd && videoId) {
      try {
        const newView: CreateVideoViewDto = {
          videoId,
          startTime: this.videoPlayStart,
          endTime: this.videoPlayEnd,
          requestAgent: '',
          type: 'stream'
        }
        const viewSaved = await firstValueFrom(this.videoService.createVideoView(newView));
      } catch (videoViewError) {
        console.warn('Error saving video view: ', videoViewError);
      }
    }
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' + secs : secs}`;
  }

  seek(event: MouseEvent | TouchEvent, video: HTMLVideoElement, progressBarElement?: HTMLElement): void {
    // Find the progress bar container if not provided
    let target: HTMLElement;
    if (progressBarElement) {
      target = progressBarElement;
    } else {
      // Try to find the progress bar container from the event
      const eventTarget = event.target as HTMLElement;
      target = eventTarget.closest('.progress-bar-container') as HTMLElement || eventTarget.parentElement?.parentElement as HTMLElement || eventTarget as HTMLElement;
    }
    
    const rect = target.getBoundingClientRect();
    const clientX = event instanceof TouchEvent ? (event.touches[0]?.clientX || event.changedTouches[0]?.clientX) : event.clientX;
    const percent = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    const newTime = percent * video.duration;
    video.currentTime = newTime;
    this.currentTime.set(newTime);
    this.progress.set(percent * 100);
  }


  startScrubbing(event: MouseEvent | TouchEvent): void {
    console.log('startScrubbing');
    this.isScrubbing.set(true);
    
    // Find the progress bar container element
    const eventTarget = event.target as HTMLElement;
    const progressBarContainer = eventTarget.closest('.progress-bar-container') as HTMLElement || 
                                 eventTarget.parentElement?.parentElement as HTMLElement ||
                                 eventTarget.parentElement as HTMLElement;
    
    const video = this.videoPlayer()!.nativeElement;
    const move = (moveEvent: MouseEvent | TouchEvent) => {
      moveEvent.preventDefault();
      this.seek(moveEvent, video, progressBarContainer);
    };
    
    const stop = () => {
      this.isScrubbing.set(false);
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', stop);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', stop);
    };

    event.preventDefault();
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', stop);
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', stop);
  }

  showLargeError() {
    const element = this.elementRef.nativeElement;
    const width = element.offsetWidth;
    return width >= 150;
  }

  ngOnDestroy(): void {
    this.saveVideoView();
  }
}
